import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  output,
  signal
} from '@angular/core';

import { ButtonComponent } from '@shared/components/button/button.component';
import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { SelectId, SelectItem } from '@shared/components/select/select-item.model';

export type ButtonSelectValue = SelectId | SelectId[] | null;

@Component({
  selector: 'app-button-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ButtonComponent, DropdownComponent],
  templateUrl: './button-select.component.html'
})
export class ButtonSelectComponent {
  items = input.required<SelectItem[]>();
  value = model<ButtonSelectValue>(null);
  multiple = input<boolean>(false);
  placeholder = input<string>('');
  searchPlaceholder = input<string>('');
  emptyMessage = input<string>('');
  triggerIconClass = input<string | null>(null);
  disabled = input<boolean>(false);
  enableSearch = input<boolean>(true);

  searchChange = output<string>();
  loadMore = output<void>();

  protected readonly isOpen = signal(false);
  protected readonly searchTerm = signal('');
  private wasNearBottom = false;

  protected readonly selectedIds = computed<SelectId[]>(() => {
    const v = this.value();
    if (v === null || v === undefined) return [];
    return Array.isArray(v) ? v : [v];
  });

  protected readonly selectedItems = computed<SelectItem[]>(() => {
    const ids = this.selectedIds();
    if (ids.length === 0) return [];
    return ids
      .map((id) => this.items().find((it) => it.id === id))
      .filter((it): it is SelectItem => it !== undefined);
  });

  protected readonly triggerLabel = computed<string>(() => {
    const sel = this.selectedItems();
    if (sel.length === 0) return this.placeholder();
    if (sel.length === 1) return sel[0].label;
    return `${sel.length} sélectionnés`;
  });

  protected toggle(): void {
    if (this.disabled()) return;
    this.isOpen.update((v) => !v);
  }

  protected close = (): void => {
    this.isOpen.set(false);
    this.searchTerm.set('');
    this.searchChange.emit('');
  };

  protected onItemClick(item: SelectItem): void {
    if (item.disabled) return;
    if (this.multiple()) {
      const ids = this.selectedIds();
      const next = ids.includes(item.id)
        ? ids.filter((id) => id !== item.id)
        : [...ids, item.id];
      this.value.set(next);
    } else {
      this.value.set(item.id);
      this.close();
    }
  }

  protected isItemSelected(item: SelectItem): boolean {
    return this.selectedIds().includes(item.id);
  }

  protected onSearchInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.searchTerm.set(term);
    this.searchChange.emit(term);
  }

  protected onListScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const threshold = 32;
    const isNearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
    if (isNearBottom && !this.wasNearBottom) {
      this.loadMore.emit();
    }
    this.wasNearBottom = isNearBottom;
  }
}
